import { useState, useEffect, useMemo } from 'react';
import { fmoClusterService } from '../services/fmoClusterService';
import { calculateFileAnalysis } from '../components/FMOPanelAlert';

/**
 * Custom hook for managing FMO cluster state and operations
 * Handles cluster fetching, filtering, selection, and updates
 */
export const useFMOClusters = (selectedRows, clusterspecsRef) => {
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Cluster data
  const [clusters, setClusters] = useState([]);
  const [clustersWithTargets, setClustersWithTargets] = useState([]);
  
  // Selection states
  const [clusterSelectionMode, setClusterSelectionMode] = useState('clusterspec');
  const [selectedCluster, setSelectedCluster] = useState('');
  const [clusterName, setClusterName] = useState(null); // null means use default
  
  // Cluster details dialog states
  const [clusterDialogOpen, setClusterDialogOpen] = useState(false);
  const [selectedClusterDetails, setSelectedClusterDetails] = useState(null);
  const [clusterNewName, setClusterNewName] = useState('');
  const [clusterNewDescription, setClusterNewDescription] = useState('');
  
  // Comparison tracking for cluster dialog
  const [currentComparisonTarget, setCurrentComparisonTarget] = useState(null);
  const [seenRowsByTarget, setSeenRowsByTarget] = useState({});
  
  // Residue Set Editor dialog states
  const [residueEditorOpen, setResidueEditorOpen] = useState(false);
  const [residueEditorMode, setResidueEditorMode] = useState(''); // 'from-clusterspec' or 'from-existing'
  const [residueEditorSourceId, setResidueEditorSourceId] = useState(null);
  
  // Analysis of selected files
  const fileAnalysis = useMemo(() => {
    return calculateFileAnalysis(selectedRows);
  }, [selectedRows]);
  
  // Filter clusters by target
  const availableClusters = useMemo(() => {
    if (!fileAnalysis.isSingleTarget) return [];
    
    const target = fileAnalysis.targets[0];
    
    // Make comparison more robust - handle case and whitespace differences
    const filtered = clustersWithTargets.filter(cluster => {
      // Skip clusters without valid targets
      if (!cluster.target || cluster.target === '<none>' || cluster.target === '<unknown>') {
        return false;
      }
      // Compare with trimmed, lowercase values for more robust matching
      return cluster.target.trim().toLowerCase() === target.trim().toLowerCase();
    });
    
    return filtered;
  }, [clustersWithTargets, fileAnalysis]);
  
  // Fetch clusters on mount
  useEffect(() => {
    const fetchClusters = async () => {
      setLoading(true);
      setLoadingMessage('Loading clusters...');
      
      try {
        const { clusters: allClusters, clustersWithTargets: clustersWithTargetInfo } = 
          await fmoClusterService.fetchClustersWithTargets(clusterspecsRef.current, false);
        
        setClusters(allClusters);
        setClustersWithTargets(clustersWithTargetInfo);
        
      } catch (error) {
        console.error('Failed to fetch cluster data:', error);
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    };
    
    // Fetch clusters (will wait for clusterspecs in ref if needed)
    fetchClusters();
  }, [clusterspecsRef]);
  
  // Handle inspect cluster
  const handleInspectCluster = async (clusterId) => {
    try {
      const clusterDetails = await fmoClusterService.getClusterDetails(clusterId);
      setSelectedClusterDetails(clusterDetails);
      setClusterNewName(clusterDetails.name || '');
      setClusterNewDescription(clusterDetails.description || '');
      
      // Track target for comparison
      const target = fileAnalysis.targets[0]; // Current target from selected files
      if (target && target !== currentComparisonTarget) {
        // Target changed, reset tracking
        setSeenRowsByTarget({});
        setCurrentComparisonTarget(target);
      } else if (target) {
        // Same target, update seen rows will happen in the display component
        setCurrentComparisonTarget(target);
      }
      
      setClusterDialogOpen(true);
      return clusterDetails;
    } catch (error) {
      console.error('Failed to fetch cluster details:', error);
      throw error;
    }
  };
  
  // Handle dialog update from cluster configuration component
  const handleDialogUpdate = (clusterDetails, target) => {
    setSelectedClusterDetails(clusterDetails);
    setClusterNewName(clusterDetails.name || '');
    setClusterNewDescription(clusterDetails.description || '');
    if (target) {
      setCurrentComparisonTarget(target);
    }
  };
  
  // Handle update cluster (name and/or description)
  const handleUpdateCluster = async () => {
    if (!selectedClusterDetails) return;
    
    // Check if anything changed
    const nameChanged = clusterNewName.trim() !== (selectedClusterDetails.name || '');
    const descriptionChanged = clusterNewDescription.trim() !== (selectedClusterDetails.description || '');
    
    if (!nameChanged && !descriptionChanged) return;
    
    try {
      await fmoClusterService.updateCluster(
        selectedClusterDetails.id_,
        clusterNewName,
        clusterNewDescription
      );
      
      // Update the cluster in the local state
      const updatedClustersWithTargets = clustersWithTargets.map(c => 
        c.id_ === selectedClusterDetails.id_ 
          ? { ...c, name: clusterNewName.trim(), groupName: clusterNewName.trim(), description: clusterNewDescription.trim() }
          : c
      );
      setClustersWithTargets(updatedClustersWithTargets);
      
      // Update the selected cluster details
      setSelectedClusterDetails(prev => ({
        ...prev,
        name: clusterNewName.trim(),
        description: clusterNewDescription.trim()
      }));
      
      return true;
    } catch (error) {
      console.error('Failed to update cluster:', error);
      throw error;
    }
  };
  
  // Reset cluster selections
  const resetClusterSelections = () => {
    setClusterSelectionMode('clusterspec');
    setSelectedCluster('');
    setClusterName(null);
  };
  
  // Update selections from config
  const updateClusterSelectionsFromConfig = (config) => {
    if (config.clusterSelectionMode !== undefined) {
      setClusterSelectionMode(config.clusterSelectionMode);
    }
    if (config.selectedCluster !== undefined) {
      setSelectedCluster(config.selectedCluster || '');
    }
    if (config.clusterName !== undefined) {
      setClusterName(config.clusterName);
    }
  };
  
  return {
    // Loading states
    loading,
    setLoading,
    loadingMessage,
    setLoadingMessage,
    
    // Cluster data
    clusters,
    setClusters,
    clustersWithTargets,
    setClustersWithTargets,
    availableClusters,
    
    // Selection states
    clusterSelectionMode,
    setClusterSelectionMode,
    selectedCluster,
    setSelectedCluster,
    clusterName,
    setClusterName,
    
    // Dialog states
    clusterDialogOpen,
    setClusterDialogOpen,
    selectedClusterDetails,
    clusterNewName,
    setClusterNewName,
    clusterNewDescription,
    setClusterNewDescription,
    
    // Comparison tracking
    currentComparisonTarget,
    setCurrentComparisonTarget,
    seenRowsByTarget,
    setSeenRowsByTarget,
    
    // Residue Editor dialog
    residueEditorOpen,
    setResidueEditorOpen,
    residueEditorMode,
    setResidueEditorMode,
    residueEditorSourceId,
    setResidueEditorSourceId,
    
    // File analysis
    fileAnalysis,
    
    // Actions
    handleInspectCluster,
    handleDialogUpdate,
    handleUpdateCluster,
    resetClusterSelections,
    updateClusterSelectionsFromConfig
  };
};